import * as React from "react";
import { useEffect, useState } from "react";
import useEntityShareState, { useSetEntityShareState } from "hooks/useEntityShareState";
import EntityShareDomain from "domainActions/permissions/EntityShareDomain";
import { createGRN } from "logic/permissions/GRN";
import { Section, Spinner } from "components/common";
import usePluggableEntityShareFormGroup from "hooks/usePluggableEntityShareFormGroup";
import GranteesList from "components/permissions/Grantee/GranteesList";
import EntityCreateCapabilitySelect from "./EntityCreateCapabilitySelect";
import {
  GranteesSelect,
  GranteesSelectOption,
  GranteesSelectorHeadline,
  ShareFormElements,
  ShareFormSection,
  ShareSubmitButton,
  StyledGranteeIcon
} from "./CommonStyledComponents";
import EntityShareValidationsDependencies from "./EntityShareValidationsDependencies";
const _renderGranteesSelectOption = ({ label, granteeType }) => /* @__PURE__ */ React.createElement(GranteesSelectOption, null, /* @__PURE__ */ React.createElement(StyledGranteeIcon, { type: granteeType }), label);
const _granteesOptions = (grantees) => grantees.map((grantee) => ({ label: grantee.title, value: grantee.id, granteeType: grantee.type })).toJS();
const getAvailableGrantee = (grantees, selected) => {
  var _a;
  return (_a = grantees == null ? void 0 : grantees.filter((g) => !selected.has(g.id))) == null ? void 0 : _a.toList();
};
const prepareShare = ({
  entityType,
  entityTitle,
  entityGRN,
  dependenciesGRN,
  selectedGranteeCapabilities
}) => {
  var _a;
  if (entityGRN) {
    return EntityShareDomain.prepare(entityType, entityTitle, entityGRN, {
      selected_grantee_capabilities: selectedGranteeCapabilities
    });
  }
  return EntityShareDomain.prepareCreate(entityType, entityTitle, {
    selected_grantee_capabilities: selectedGranteeCapabilities,
    prepare_request: ((_a = selectedGranteeCapabilities == null ? void 0 : selectedGranteeCapabilities.size) != null ? _a : 0) > 0 ? dependenciesGRN : null
  });
};
const EntityCreateShareFormGroup = ({
  description,
  entityType,
  entityTitle = "",
  onSetEntityShare,
  entityId = null,
  entityTypeTitle = "",
  dependenciesGRN = null,
  defaultSharePayload = void 0
}) => {
  var _a;
  const entityGRN = entityId && createGRN(entityType, entityId);
  const { data: entityShareState } = useEntityShareState(entityGRN);
  const setEntityShareState = useSetEntityShareState();
  const defaultShareSelection = { granteeId: null, capabilityId: "view" };
  const [disableSubmit, setDisableSubmit] = useState((_a = entityShareState == null ? void 0 : entityShareState.validationResults) == null ? void 0 : _a.failed);
  const [shareSelection, setShareSelection] = useState(defaultShareSelection);
  const [entityShare, setEntityShare] = useState(defaultSharePayload != null ? defaultSharePayload : null);
  const PluggableEntityShareFormGroup = usePluggableEntityShareFormGroup();
  useEffect(() => {
    prepareShare({
      entityType,
      entityTitle,
      entityGRN,
      dependenciesGRN,
      selectedGranteeCapabilities: defaultSharePayload == null ? void 0 : defaultSharePayload.selected_grantee_capabilities
    }).then((state) => {
      setEntityShareState(entityGRN, state);
    });
  }, [entityType, entityTitle, entityGRN, defaultSharePayload, dependenciesGRN, setEntityShareState]);
  const resetSelection = () => {
    setDisableSubmit(false);
    setShareSelection(defaultShareSelection);
  };
  const handleSelection = ({ granteeId, capabilityId }) => {
    const newSelectedCapabilities = entityShareState == null ? void 0 : entityShareState.selectedGranteeCapabilities.merge({ [granteeId]: capabilityId });
    setDisableSubmit(true);
    setEntityShare({ ...entityShare, selected_grantee_capabilities: newSelectedCapabilities });
    return prepareShare({
      entityType,
      entityTitle,
      entityGRN,
      dependenciesGRN,
      selectedGranteeCapabilities: newSelectedCapabilities
    }).then((response) => {
      setEntityShareState(entityGRN, response);
      onSetEntityShare({ ...entityShare, selected_grantee_capabilities: newSelectedCapabilities });
      resetSelection();
      return response;
    });
  };
  const handleDeletion = (granteeId) => {
    const newSelectedCapabilities = entityShareState == null ? void 0 : entityShareState.selectedGranteeCapabilities.remove(granteeId);
    setDisableSubmit(true);
    setEntityShare({ ...entityShare, selected_grantee_capabilities: newSelectedCapabilities });
    return prepareShare({
      entityType,
      entityTitle,
      entityGRN,
      dependenciesGRN,
      selectedGranteeCapabilities: newSelectedCapabilities
    }).then((response) => {
      setEntityShareState(entityGRN, response);
      onSetEntityShare({ ...entityShare, selected_grantee_capabilities: newSelectedCapabilities });
      setDisableSubmit(false);
      return response;
    });
  };
  const handleAdditionalFormChange = (values) => {
    const newEntityShare = { ...entityShare, ...values };
    setEntityShare(newEntityShare);
    onSetEntityShare(newEntityShare);
  };
  const handleAddCollaborator = () => {
    handleSelection(shareSelection);
  };
  return /* @__PURE__ */ React.createElement(Section, { title: "" }, entityShareState ? /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(ShareFormSection, null, /* @__PURE__ */ React.createElement(GranteesSelectorHeadline, null, "Add Collaborator"), /* @__PURE__ */ React.createElement("p", null, description), /* @__PURE__ */ React.createElement(ShareFormElements, null, /* @__PURE__ */ React.createElement(
    GranteesSelect,
    {
      onChange: (granteeId) => setShareSelection({ ...shareSelection, granteeId }),
      optionRenderer: _renderGranteesSelectOption,
      options: _granteesOptions(
        getAvailableGrantee(entityShareState.availableGrantees, entityShareState.selectedGranteeCapabilities)
      ),
      placeholder: "Search for users and teams",
      value: shareSelection.granteeId
    }
  ), /* @__PURE__ */ React.createElement(
    EntityCreateCapabilitySelect,
    {
      onChange: (capabilityId) => setShareSelection({ ...shareSelection, capabilityId }),
      capabilities: entityShareState == null ? void 0 : entityShareState.availableCapabilities,
      value: shareSelection.capabilityId
    }
  ), /* @__PURE__ */ React.createElement(
    ShareSubmitButton,
    {
      bsStyle: "primary",
      title: "Add Collaborator",
      onClick: handleAddCollaborator,
      disabled: disableSubmit || !shareSelection.granteeId
    },
    "Add Collaborator"
  ))), /* @__PURE__ */ React.createElement(ShareFormSection, null, /* @__PURE__ */ React.createElement(
    GranteesList,
    {
      activeShares: entityShareState == null ? void 0 : entityShareState.activeShares,
      availableCapabilities: entityShareState == null ? void 0 : entityShareState.availableCapabilities,
      entityType,
      entityTypeTitle,
      onDelete: handleDeletion,
      onCapabilityChange: handleSelection,
      selectedGrantees: entityShareState == null ? void 0 : entityShareState.selectedGrantees,
      title: "Collaborators",
      isCreating: true
    }
  )), /* @__PURE__ */ React.createElement(
    EntityShareValidationsDependencies,
    {
      missingDependencies: entityShareState.missingDependencies,
      validationResults: entityShareState.validationResults,
      availableGrantees: entityShareState.availableGrantees
    }
  ), PluggableEntityShareFormGroup && /* Resolved from the plugin store at render time and cannot be hoisted. */
  /* eslint-disable-next-line react-hooks/static-components */
  /* @__PURE__ */ React.createElement(
    PluggableEntityShareFormGroup,
    {
      entityType,
      onChange: handleAdditionalFormChange,
      value: (defaultSharePayload == null ? void 0 : defaultSharePayload.selected_collections) || []
    }
  )) : /* @__PURE__ */ React.createElement(Spinner, null));
};
export default EntityCreateShareFormGroup;
